import { Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import moment from 'moment';
import { DATE_ISO_FORMAT } from '@constants/date.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { AppointmentServiceModel } from '@models/appointment-service.model';
import { DayWiseFreeSlotsDao } from '@models/availability.model';
import { AppointmentCartItem } from '@models/selected-booking-data.model';
import { StoreDbModel } from '@models/store-db.model';
import { WorkerDbModel } from '@models/worker-db.model';
import { CalioDateUtcPipe } from '@pipes/calio-date-utc.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { BookingService } from '@services/feature/booking.service';
import { TimezoneService } from '@services/feature/timezone.service';
import { WidgetService } from '@services/feature/widget.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { LoggerService } from '@services/utils/logger.service';
import { resolveSlotWorkerUuids, servicesRequestResources } from '@services/utils/slot-workers.util';
import { expandCollapse } from '@animations/expand-collapse';

interface FlatSlot {
  start: string;
  end: string;
  shortStart: string;
  startHour: number;
  worker: WorkerDbModel;
  workerUuids: string[];
  store: StoreDbModel;
  combiServiceWorkersMapping: Record<number, number>;
}

interface RelatedServiceSuggestion {
  cartItem: AppointmentCartItem;
  relatedService: AppointmentServiceModel;
  availableSlots: FlatSlot[];
  isAddedToCart: boolean;
  combinedBookingText: string;
}

const pipes = [CalioDateUtcPipe, CalioSafePipe];

@Component({
  selector: 'app-related-service-suggestions',
  templateUrl: './related-service-suggestions.component.html',
  styleUrls: ['./related-service-suggestions.component.scss'],
  animations: [expandCollapse],
  imports: [...pipes, TranslateModule]
})
export class RelatedServiceSuggestionsComponent implements OnInit {

  @Output() addRelatedServiceEvent = new EventEmitter<{
    cartItem: AppointmentCartItem;
    slot: FlatSlot;
    relatedService: AppointmentServiceModel;
  }>();

  protected bookingService = inject(BookingService);
  protected widgetService = inject(WidgetService);
  private timezoneService = inject(TimezoneService);
  private dateUtilService = inject(DateUtilService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  protected lang: string;
  protected relatedServiceSuggestions: RelatedServiceSuggestion[] = [];
  protected isLoading = false;

  get timeFormat(): string {
    return this.dateUtilService.userTimeFormat;
  }

  ngOnInit(): void {
    this.lang = this.translate.currentLang || this.translate.getDefaultLang();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.lang = event.lang;
        this.rebuildCombinedBookingTexts();
      });

    this.findRelatedServices();
  }

  private findRelatedServices(): void {
    const cartItems = this.bookingService.selectedBookingData.cartItems;
    if (!cartItems?.length) return;

    // Collect cart items that have a related service not yet in cart
    const itemsWithRelated: { cartItem: AppointmentCartItem; relatedServiceId: number }[] = [];

    for (const cartItem of cartItems) {
      const relatedId = cartItem.service?.related_appointment_service_id;
      if (!relatedId) continue;

      // Skip if related service is already in the cart
      const alreadyInCart = cartItems.some(ci => ci.serviceId === relatedId);
      if (alreadyInCart) continue;

      itemsWithRelated.push({ cartItem, relatedServiceId: relatedId });
    }

    if (!itemsWithRelated.length) return;

    this.isLoading = true;

    // Fetch all services for the store to get the related service model
    const storeId = itemsWithRelated[0].cartItem.storeId;
    this.bookingService.getAppointmentSevices({ store_id: storeId, worker_id: 0 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (allServices: AppointmentServiceModel[]) => {
          this.fetchSlotsForRelatedServices(itemsWithRelated, allServices);
        },
        error: (error) => {
          LoggerService.error(error);
          this.isLoading = false;
        }
      });
  }

  private fetchSlotsForRelatedServices(
    itemsWithRelated: { cartItem: AppointmentCartItem; relatedServiceId: number }[],
    allServices: AppointmentServiceModel[]
  ): void {
    const slotRequests: {
      cartItem: AppointmentCartItem;
      relatedService: AppointmentServiceModel;
      request$: ReturnType<BookingService['getDaysWiseFreeSlots']>;
    }[] = [];

    for (const { cartItem, relatedServiceId } of itemsWithRelated) {
      const relatedService = allServices.find(s => s.id === relatedServiceId);
      if (!relatedService) continue;

      const formattedDate = this.dateUtilService.getFormattedDateByFormat(cartItem.date, DATE_ISO_FORMAT);
      const timezone = this.bookingService.partnerData?.is_timezone_enabled
        ? this.bookingService.effectiveDisplayTimezone
        : undefined;

      const request$ = this.bookingService.getDaysWiseFreeSlots(
        0,
        cartItem.storeId,
        relatedService.id.toString(),
        formattedDate,
        timezone
      );

      slotRequests.push({ cartItem, relatedService, request$ });
    }

    if (!slotRequests.length) {
      this.isLoading = false;
      return;
    }

    forkJoin(slotRequests.map(r => r.request$))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (responses: DayWiseFreeSlotsDao[]) => {
          responses.forEach((response, index) => {
            const { cartItem, relatedService } = slotRequests[index];
            this.setupSlots(cartItem, relatedService, response);
          });
          this.isLoading = false;
        },
        error: (error) => {
          LoggerService.error(error);
          this.isLoading = false;
        }
      });
  }

  private setupSlots(
    cartItem: AppointmentCartItem,
    relatedService: AppointmentServiceModel,
    response: DayWiseFreeSlotsDao
  ): void {
    if (!response?.available_minutes_blocks?.length) return;

    // Flatten slots (pattern from slots-selection.component.ts:296-319)
    const allSlots: FlatSlot[] = [];
    response.available_minutes_blocks.forEach(daysWiseSlots => {
      daysWiseSlots.workers_minutes_blocks?.forEach(workerMinutesBlock => {
        workerMinutesBlock.minutes_blocks.forEach(availabilities => {
          availabilities.appointments_slots?.forEach(slot => {
            // The related-service request above always asks for "Anyone" (worker_id 0).
            const workerUuids = resolveSlotWorkerUuids(workerMinutesBlock, true, servicesRequestResources([relatedService]));
            allSlots.push({
              start: slot.appointment_start,
              end: slot.appointment_end,
              shortStart: slot.appointment_short_start,
              startHour: parseInt(slot.appointment_start.split(' ')[1]?.split(':')[0], 10),
              worker: workerMinutesBlock.worker,
              workerUuids,
              store: workerMinutesBlock.store,
              combiServiceWorkersMapping: slot.combiServiceWorkersMapping || workerMinutesBlock.combi_workers || {},
            });
          });
        });
      });
    });

    // Filter: adjacent to the primary appointment (before or after)
    const adjacentSlots: FlatSlot[] = [];
    for (const slot of allSlots) {
      const isBefore = moment.utc(slot.end).isSame(moment.utc(cartItem.startTime));
      const isAfter = moment.utc(slot.start).isSame(moment.utc(cartItem.endTime));

      if (!isBefore && !isAfter) continue;

      // Conflict check: ensure no overlap with ANY cart item
      const cartItems = this.bookingService.selectedBookingData.cartItems;
      const hasConflict = cartItems.some(ci => {
        const slotStart = moment.utc(slot.start);
        const slotEnd = moment.utc(slot.end);
        const ciStart = moment.utc(ci.startTime);
        const ciEnd = moment.utc(ci.endTime);

        return (slotStart.isSameOrAfter(ciStart) && slotStart.isBefore(ciEnd)) ||
               (slotStart.isBefore(ciStart) && slotEnd.isAfter(ciStart));
      });

      if (!hasConflict) {
        adjacentSlots.push(slot);
      }
    }

    if (!adjacentSlots.length) return;

    this.relatedServiceSuggestions.push({
      cartItem,
      relatedService,
      availableSlots: adjacentSlots,
      isAddedToCart: false,
      combinedBookingText: this.buildCombinedBookingText(cartItem.service, relatedService),
    });
  }

  private buildCombinedBookingText(originalService: AppointmentServiceModel, relatedService: AppointmentServiceModel): string {
    const template = this.bookingService.widgetTemplates?.find(
      t => t.id === TEMPLATE_CONSTANT.WIDGET_COMBINED_BOOKING_TEXT
    );

    if (!template) return '';

    let text: string;
    if (template._translations?.[this.lang]?.template_content) {
      text = template._translations[this.lang].template_content;
    } else {
      text = template.template_content || '';
    }

    const originalName = this.getTranslatedName(originalService);
    const relatedName = this.getTranslatedName(relatedService);

    text = text.replaceAll('[SERVICE_NAME]', originalName);
    text = text.replaceAll('[RELATED_SERVICE_NAME]', relatedName);

    return text;
  }

  private getTranslatedName(service: AppointmentServiceModel): string {
    if (service?.is_multi_language === 1 && service?._translations?.[this.lang]?.name) {
      return service._translations[this.lang].name;
    }
    return service?.name || '';
  }

  private rebuildCombinedBookingTexts(): void {
    for (const suggestion of this.relatedServiceSuggestions) {
      suggestion.combinedBookingText = this.buildCombinedBookingText(
        suggestion.cartItem.service,
        suggestion.relatedService
      );
    }
  }

  protected onSelectSlot(suggestion: RelatedServiceSuggestion, slot: FlatSlot): void {
    suggestion.isAddedToCart = true;
    this.addRelatedServiceEvent.emit({
      cartItem: suggestion.cartItem,
      slot,
      relatedService: suggestion.relatedService,
    });
  }
}
